import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import logo from "../Assets/images/mlogo.png";

const NewsCard = ({
  image,
  category,
  state,
  title,
  author,
  reporter_image,
  date,
  readTime,
  fullNews,
  videoUrl,
  link,
  id,
  hideListenButton = false,
}) => {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [speaking, setSpeaking] = useState(false);

  const synthRef = useRef(window.speechSynthesis);

  useEffect(() => {
    const synth = synthRef.current;

    const loadVoices = () => {
      synth.getVoices();
    };

    loadVoices();

    if (synth.onvoiceschanged !== undefined) {
      synth.onvoiceschanged = loadVoices;
    }

    return () => {
      synth.cancel();
    };
  }, []);

  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  const cleanText = (text) => {
    if (!text) return "";

    return text
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  const displayCategory = state ? state : (category && category.trim() ? category : "सामान्य");

  // =========================
  // SHARE FUNCTION
  // =========================
 const handleShare = async (e) => {
  e.stopPropagation();

  try {
    const shareUrl = new URL(link, window.location.origin).href;

    if (navigator.share) {
      await navigator.share({
        title: title || "News",
        text: title || "Check this news",
        url: shareUrl,
      });
    } else {
      await navigator.clipboard.writeText(shareUrl);
      alert("लिंक सफलतापूर्वक कॉपी हो गया!");
    }
  } catch (error) {
    console.log("Share failed:", error);
  }
};

  // =========================
  // TEXT TO SPEECH
  // =========================
  const handleListen = (e) => {
    e.stopPropagation();

    const synth = synthRef.current;

    if (speaking) {
      synth.cancel();
      setSpeaking(false);
      return;
    }

    synth.cancel();

    const cleanedText = cleanText(fullNews);

    const voices = synth.getVoices();

    const hindiVoice =
      voices.find((v) => v.lang === "hi-IN") ||
      voices.find((v) => v.lang.includes("hi"));

    const chunks = cleanedText?.match(/.{1,180}/g);

    let index = 0;

    const speakChunk = () => {
      if (!chunks || index >= chunks.length) {
        setSpeaking(false);
        return;
      }

      const utterance = new SpeechSynthesisUtterance(chunks[index]);

      if (hindiVoice) {
        utterance.voice = hindiVoice;
      } else {
        utterance.lang = "hi-IN";
      }

      utterance.rate = 0.85;
      utterance.pitch = 1;

      utterance.onend = () => {
        index++;
        speakChunk();
      };

      utterance.onerror = () => {
        setSpeaking(false);
      };

      synth.speak(utterance);
    };

    speakChunk();
    setSpeaking(true);
  };

  const targetLink = link || (id ? `/news/${id}` : "/news/1");

  return (
    <div
      onClick={() => navigate(targetLink)}
      className="group flex flex-col gap-3 cursor-pointer bg-white h-full border border-gray-200/70 rounded-xl p-3 hover:border-gray-300 hover:shadow-sm transition-all duration-300"
    >
      {/* IMAGE CONTAINER */}
      <div className="relative aspect-[16/9] w-full overflow-hidden rounded-sm bg-gray-100">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />

        {/* VIDEO PLAY BUTTON */}
        {videoUrl && (
          <a
            href={videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/10 transition"
          >
            <div className="w-12 h-12 rounded-full bg-brand-red/90 text-white flex items-center justify-center shadow-md">
              <i className="fa-solid fa-play text-lg ml-1"></i>
            </div>
          </a>
        )}

        {/* LOGO (Styled like Header) */}
        <div className="absolute top-2 right-2">
          <img
            src={logo}
            alt="logo"
            className="w-10 h-10 object-contain bg-white border-[2px] border-white ring-[1px] ring-black shadow-[0_0_15px_rgba(0,0,0,0.4)] rounded-full"
          />
        </div>
      </div>

      {/* CONTENT BLOCK */}
      <div className="flex flex-col flex-1 relative px-3 pb-3 pt-1">
        {/* Subtle Category */}
        <span className="text-brand-red text-[11px] font-black uppercase tracking-wider mb-1">
          {displayCategory}
        </span>

        {/* BOLD HEADLINE (Clamped to 3 lines for uniform height) */}
        <h3 
          onClick={(e) => {
            e.stopPropagation();
            navigate(targetLink);
          }}
          className="font-normal text-[18px] md:text-[20px] leading-snug text-black group-hover:text-brand-red transition-colors line-clamp-3"
        >
          {title}
        </h3>

        {/* AUTHOR & TIME (Minimalist TOI style) */}
        <div className="mt-2 text-[11px] text-gray-500 flex items-center gap-2 font-medium tracking-wide">
          <span className="uppercase text-gray-600 font-bold">{author || "न्यूज़ डेस्क"}</span>
          <span>|</span>
          <span>{date}</span>
        </div>

        {/* SLEEK ACTION TOOLBAR (Listen, Read, Share) pushed to bottom */}
        <div className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-between opacity-90 group-hover:opacity-100 transition-opacity">
          
          <div className="flex items-center gap-3.5">
            <button
              onClick={(e) => {
                  if (videoUrl) {
                      e.stopPropagation();
                      window.open(videoUrl, "_blank");
                  } else {
                      e.stopPropagation();
                      setOpen(true);
                  }
              }}
              className="text-red-500 hover:text-red-600 transition transform hover:scale-110" title="वीडियो चलाएं"
            >
              <i className="fa-solid fa-circle-play text-[15px] md:text-[17px]"></i>
            </button>
            {!hideListenButton && (
              <button
                onClick={handleListen}
                className={`transition transform hover:scale-110 ${speaking ? 'text-brand-red animate-pulse' : 'text-blue-500 hover:text-blue-600'}`}
                title="ध्वनि"
              >
                <i className={`fa-solid ${speaking ? 'fa-volume-xmark' : 'fa-volume-high'} text-[14px] md:text-[16px]`}></i>
              </button>
            )}
          </div>

          <div className="flex items-center gap-3.5">
            <button
              onClick={handleShare}
              className="text-green-500 hover:text-green-600 transition transform hover:scale-110" title="साझा करें"
            >
              <i className="fa-solid fa-share-nodes text-[14px] md:text-[16px]"></i>
            </button>
            <button
              onClick={(e) => {
                  e.stopPropagation();
                  setOpen(true);
              }}
              className="text-gray-400 hover:text-gray-600 transition transform hover:scale-110" title="अधिक"
            >
              <i className="fa-solid fa-ellipsis-vertical text-[14px] md:text-[16px]"></i>
            </button>
          </div>
        </div>

        {/* FULL NEWS OVERLAY (Unchanged logic, just cleaner UI) */}
        <div
          onClick={(e) => e.stopPropagation()}
          className={`absolute bottom-0 left-0 right-0 bg-white z-40 transition-all duration-300 ease-in-out border-t-2 border-brand-red shadow-[0_-10px_20px_rgba(0,0,0,0.1)]
          ${open ? "h-auto max-h-full opacity-100 visible p-4" : "h-0 opacity-0 invisible p-0 overflow-hidden"}`}
        >
          <div className="flex justify-between items-center mb-3">
            <span className="font-bold text-sm text-black">विस्तार से पढ़ें</span>
            <button
              onClick={() => {
                setOpen(false);
                synthRef.current.cancel();
                setSpeaking(false);
              }}
              className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700"
            >
              <i className="fa-solid fa-xmark text-xs"></i>
            </button>
          </div>
          <div className="text-sm text-gray-800 leading-relaxed max-h-[150px] overflow-y-auto mb-3 pr-2 scrollbar-thin text-justify">
            {fullNews}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsCard;